/**
 * <SettingsSection /> — section wrapper on the Settings hub landing.
 *
 * Per spec §1.A.2: each section has:
 *   - Section title (h2) with a thin underline
 *   - Optional 1-line subtitle
 *   - Grid of <SettingsEntryCard /> children
 *   - Bilingual via t() (caller passes already-translated strings)
 *   - Mobile: 1 col; tablet: 2 cols; desktop: 3-5 cols depending on content
 */

import type { ReactNode } from "react";

export interface SettingsSectionProps {
  /** Pre-translated section title. */
  title: string;
  /** Optional 1-line section subtitle. */
  subtitle?: string;
  /** SettingsEntryCard children. */
  children: ReactNode;
}

export function SettingsSection({
  title,
  subtitle,
  children,
}: SettingsSectionProps) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3 border-b border-border-subtle pb-1.5">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-text-secondary">
          {title}
        </h2>
        {subtitle && (
          <p className="text-xs text-text-tertiary">{subtitle}</p>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {children}
      </div>
    </section>
  );
}

export default SettingsSection;
